import { Box, Container, Typography, Divider, useTheme, Paper, ListItemIcon } from '@mui/material';
import { Helmet } from 'react-helmet';
import { useEffect } from 'react';
import CheckIcon from '@mui/icons-material/Check';

const PrivacyPolicyPage = () => {
  const theme = useTheme();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box sx={{ py: { xs: 10, md: 12 } }}>
      <Helmet>
        <title>Privacy Policy | SOLVENDA ISP Management Solution</title>
        <meta name="description" content="SOLVENDA's Privacy Policy explains how we collect, use, and protect your information when using our ISP management platform and services." />
        <meta name="keywords" content="SOLVENDA privacy, ISP management privacy, data protection, privacy policy, SOLVENDA data security" />
        <link rel="canonical" href="https://solvenda.org/privacy-policy" />
      </Helmet>
      <Container maxWidth="md">
        <Paper elevation={1} sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h1" component="h1" gutterBottom>
            Privacy Policy
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
            Last Updated: October 15, 2024
          </Typography>

          <Typography variant="body1" paragraph>
            This Privacy Policy explains how SOLVENDA collects, uses, and protects your information when you use our website and services.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h5" component="h2" gutterBottom>
            1. Information We Collect
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" gutterBottom>
              <strong>Personal Information:</strong> Name, email, company details, billing information, and service preferences.
            </Typography>
            
            <Typography variant="body1">
              <strong>Technical Information:</strong> IP address, browser type, device information, and usage data.
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h5" component="h2" gutterBottom>
            2. How We Use Your Information
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Box sx={{ 
              bgcolor: theme.palette.background.paper, 
              p: 2, 
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              minWidth: '150px',
              flex: '1 1 45%'
            }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Service Provision
              </Typography>
              <Typography variant="body2">
                Delivering and maintaining our ISP management solutions
              </Typography>
            </Box>
            
            <Box sx={{ 
              bgcolor: theme.palette.background.paper, 
              p: 2, 
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              minWidth: '150px',
              flex: '1 1 45%'
            }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Transactions
              </Typography>
              <Typography variant="body2">
                Processing payments and billing
              </Typography>
            </Box>
            
            <Box sx={{ 
              bgcolor: theme.palette.background.paper, 
              p: 2, 
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              minWidth: '150px',
              flex: '1 1 45%'
            }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Support
              </Typography>
              <Typography variant="body2">
                Providing customer and technical assistance
              </Typography>
            </Box>
            
            <Box sx={{ 
              bgcolor: theme.palette.background.paper, 
              p: 2, 
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              minWidth: '150px',
              flex: '1 1 45%'
            }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Improvement
              </Typography>
              <Typography variant="body2">
                Enhancing our services and user experience
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h5" component="h2" gutterBottom>
            3. Data Security
          </Typography>
          
          <Typography variant="body1" paragraph>
            We implement appropriate security measures to protect your data but cannot guarantee 100% security for internet transmissions.
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <CheckIcon color="primary" />
            </ListItemIcon>
            <Typography variant="body1">Data encryption in transit and at rest</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <CheckIcon color="primary" />
            </ListItemIcon>
            <Typography variant="body1">Regular security assessments</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <CheckIcon color="primary" />
            </ListItemIcon>
            <Typography variant="body1">Access controls and monitoring</Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h5" component="h2" gutterBottom>
            4. Data Sharing
          </Typography>
          
          <Typography variant="body1" paragraph>
            We do not sell your personal information. We may share data with:
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <CheckIcon color="primary" />
            </ListItemIcon>
            <Typography variant="body1">Service providers who help us operate</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <CheckIcon color="primary" />
            </ListItemIcon>
            <Typography variant="body1">Legal authorities when required by law</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <CheckIcon color="primary" />
            </ListItemIcon>
            <Typography variant="body1">Business partners during a merger or acquisition</Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h5" component="h2" gutterBottom>
            5. Your Rights
          </Typography>
          
          <Typography variant="body1" paragraph>
            You have the right to:
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <Typography variant="body2">Access your data</Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <Typography variant="body2">Request corrections</Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <Typography variant="body2">Delete your information</Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <Typography variant="body2">Restrict processing</Typography>
            </Box>
          </Box>
          
          <Typography variant="body1" paragraph>
            To exercise these rights, contact us at <strong>privacy@solvenda.com</strong>
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h5" component="h2" gutterBottom>
            6. Cookies
          </Typography>
          
          <Typography variant="body1" paragraph>
            We use cookies to improve your experience. You can manage cookie settings in your browser.
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Box sx={{ flex: '1 1 45%', minWidth: '150px' }}>
              <Typography variant="body2" fontWeight="bold">Essential Cookies</Typography>
              <Typography variant="body2">Required for basic site functionality</Typography>
            </Box>
            
            <Box sx={{ flex: '1 1 45%', minWidth: '150px' }}>
              <Typography variant="body2" fontWeight="bold">Analytics Cookies</Typography>
              <Typography variant="body2">Help us understand site usage</Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h5" component="h2" gutterBottom>
            7. Updates to This Policy
          </Typography>
          
          <Typography variant="body1" paragraph>
            We may update this policy periodically. Significant changes will be notified by updating the date at the top of this page.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h5" component="h2" gutterBottom>
            8. Contact Us
          </Typography>
          
          <Box sx={{ 
            bgcolor: theme.palette.background.paper, 
            p: 3, 
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            textAlign: 'center',
            mb: 2
          }}>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
              SOLVENDA
            </Typography>
            <Typography variant="body1">
              Email: info@solvenda.org<br />
              Address: Tirana, Albania<br />
              Phone: +355 69 209 8563
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicyPage; 